// ---------------------------------------------------------------------------
// Scenario factory — TMP-style operational (scenario) telemetry.
//
// Mirrors the teams-modular-packages `scenarioFactory` surface so instrumenting
// AI Studio feels familiar:
//
//   const { scenarioFactory } = useAppContext();
//   const scenario = scenarioFactory.newScenario('load_datasets');
//   scenario.mark('fetch_started');
//   scenario.complete('success');
//
// `newScenario` opens a NEW scenario instance with a fresh correlation queryId
// (GUID) and fires the create event; the returned `Scenario` handle's
// `mark`/`complete` are pre-bound to that instance's name + queryId. Scenario
// names are the typed `ScenarioName` union.
//
// Framework-agnostic (no React): the factory is built from a `TelemetryLogger`
// (or null) by `<AppProvider>` and exposed on AppContext. No-op-safe — when the
// logger is null every scenario handle is a stable no-op.
// ---------------------------------------------------------------------------

import type { TelemetryLogger } from './logger.js';
import type {
  IScenario,
  IScenarioFactory,
  ScenarioData,
  ScenarioStartOptions,
  ScenarioStatus,
} from './scenarios.interface.js';
import type { ScenarioIdentifier } from './scenarios.js';

export class Scenario implements IScenario {
  constructor(
    private readonly name: ScenarioIdentifier,
    public readonly queryId: string,
    private readonly logger?: TelemetryLogger,
  ) {}

  mark(step: string, data?: Record<string, ScenarioData>): void {
    this.logger?.scenario.mark({
      name: this.name,
      step,
      queryId: this.queryId,
      data,
    });
  }

  complete(status: ScenarioStatus, data?: Record<string, ScenarioData>): void {
    this.logger?.scenario.complete({
      name: this.name,
      status,
      queryId: this.queryId,
      data,
    });
  }
}

export class ScenarioFactory implements IScenarioFactory {
  constructor(private readonly logger?: TelemetryLogger) {}

  newScenario(
    name: ScenarioIdentifier,
    options?: ScenarioStartOptions,
  ): IScenario {
    if (!this.logger) return new Scenario(name, '');

    const queryId = this.newQueryId();
    this.logger.scenario.create({
      name,
      queryId,
      timeout: options?.timeout,
      abort: options?.abort,
    });

    return new Scenario(name, queryId, this.logger);
  }

  private newQueryId(): string {
    return crypto.randomUUID();
  }
}

export function createScenarioFactory(
  logger?: TelemetryLogger,
): IScenarioFactory {
  return new ScenarioFactory(logger);
}
